"use client"

import { useState, useRef, useEffect } from "react"
import "./Register.css"
import { useLocation, useNavigate } from "react-router-dom"
import { appName, API_V1 } from "../../Constant"
import { toastFunction } from "../../Util"
import { useAuth } from "../../Context/AuthContext"
import { useGstinLookup } from "../../hooks/useGstinLookup"


// GST state code map — mirrors backend INDIA_GST_STATE_CODES
const GST_STATE_CODES = {
  '01': 'Jammu and Kashmir', '02': 'Himachal Pradesh', '03': 'Punjab',
  '04': 'Chandigarh', '05': 'Uttarakhand', '06': 'Haryana', '07': 'Delhi',
  '08': 'Rajasthan', '09': 'Uttar Pradesh', '10': 'Bihar', '11': 'Sikkim',
  '12': 'Arunachal Pradesh', '13': 'Nagaland', '14': 'Manipur', '15': 'Mizoram',
  '16': 'Tripura', '17': 'Meghalaya', '18': 'Assam', '19': 'West Bengal',
  '20': 'Jharkhand', '21': 'Odisha', '22': 'Chhattisgarh', '23': 'Madhya Pradesh',
  '24': 'Gujarat', '26': 'Dadra and Nagar Haveli and Daman and Diu',
  '27': 'Maharashtra', '29': 'Karnataka', '30': 'Goa', '31': 'Lakshadweep',
  '32': 'Kerala', '33': 'Tamil Nadu', '34': 'Puducherry',
  '35': 'Andaman and Nicobar Islands', '36': 'Telangana', '37': 'Andhra Pradesh',
  '38': 'Ladakh', '97': 'Other Territory', '99': 'Centre Jurisdiction',
};

export const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

// Pure function: maps a GSTIN lookup result onto form state.
// Exported for unit testing; used inline by handleGstinBlur.
export function applyGstinToFormFields(details, prev) {
  const nameParts = (details.displayName || '').trim().split(/\s+/);
  return {
    ...prev,
    firstName:   nameParts.length > 1 ? nameParts.slice(0, -1).join(' ') : (nameParts[0] || prev.firstName),
    lastName:    nameParts.length > 1 ? nameParts[nameParts.length - 1]   : prev.lastName,
    companyName: details.legalName           || prev.companyName,
    stateCode:   details.stateCode           || prev.stateCode,
    addressLine1: details.address?.addressLine1 || prev.addressLine1,
    city:         details.address?.city         || prev.city,
    pincode:      details.address?.pincode      || prev.pincode,
  };
}

const deriveStateCodeFromGSTIN = (gstin) => {
  if (!gstin || gstin.length < 2) return null;
  const code = gstin.substring(0, 2);
  return GST_STATE_CODES[code] ? code : null;
};

const RegisterComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login, logout, refreshAccessToken } = useAuth()
  const { lookupGstin, loading: gstinLoading } = useGstinLookup()

  const [isLoading, setIsLoading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [tosAccepted, setTosAccepted] = useState(false)
  const fileInputRef = useRef(null)

  // Form data state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    gstNumber: "",
    stateCode: "",
    companyName: "",
    subscriptionCode: "TRIAL",
    addressLine1: "",
    city: "",
    pincode: "",
    logo: null,
  })

  // Form errors state
  const [errors, setErrors] = useState({})


  useEffect(() => {
    if (location.state) {
      const { token, email, idToken, phoneNumber, isEmail } = location.state;
      if(isEmail){
        setFormData((prev) => ({
          ...prev,
          token,
          email,
        }));
      }else{
        setFormData((prev) => ({
          ...prev,
          idToken,
          phoneNumber,
        }));
      }
    }
  },[location.state]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target

    const updates = { [name]: value };

    // Auto-derive stateCode from GSTIN when it becomes valid
    if (name === "gstNumber") {
      const upper = value.toUpperCase();
      updates.gstNumber = upper;
      const derived = deriveStateCodeFromGSTIN(upper);
      if (derived) updates.stateCode = derived;
    }

    setFormData((prev) => ({ ...prev, ...updates }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }))
    }
  }

  // Fetch business details for a well-formed GSTIN and fill in still-empty fields.
  // GSTIN is optional — a failed/not-found lookup is a silent no-op, never a blocker.
  const handleGstinBlur = async () => {
    if (!formData.gstNumber || !GSTIN_REGEX.test(formData.gstNumber)) return
    const details = await lookupGstin(formData.gstNumber)
    if (!details) return
    setFormData((prev) => applyGstinToFormFields(details, prev))
  }

  // Handle file upload
  const handleFileUpload = (file) => {
    // Validate file type
    if (file && !file.type.startsWith("image/")) {
      toastFunction("warning","Please upload an image file (PNG, JPG, JPEG, SVG)");
      return
    }

    // Validate file size (max 5MB)
    if (file && file.size > 5 * 1024 * 1024) {
      toastFunction("warning","File size should be less than 5MB");
      return
    }

    setFormData((prev) => ({
      ...prev,
      logo: file,
    }))
  }

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setDragOver(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    const files = e.dataTransfer.files
    if (files.length > 0) {
      handleFileUpload(files[0])
    }
  }

  // Handle file input change
  const handleFileInputChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      handleFileUpload(file)
    }
  }

  // Remove uploaded file
  const handleRemoveFile = () => {
    setFormData((prev) => ({
      ...prev,
      logo: null,
    }))
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required"
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required"
    }

    if (!formData.companyName.trim()) {
      newErrors.companyName = "Company name is required"
    }

    // GST number validation (optional but if provided, should be valid format)
    if (formData.gstNumber && !GSTIN_REGEX.test(formData.gstNumber)) {
      newErrors.gstNumber = "Please enter a valid GST number"
    }

    // stateCode is required — can be derived from GSTIN or selected manually
    if (!formData.stateCode) {
      newErrors.stateCode = formData.gstNumber
        ? "Could not derive state from this GSTIN. Please select manually."
        : "State is required for GST calculations"
    }

    // GSTIN ↔ state mismatch: block submission if a valid GSTIN implies a different state
    const derivedAtValidation = GSTIN_REGEX.test(formData.gstNumber)
      ? deriveStateCodeFromGSTIN(formData.gstNumber)
      : null
    if (derivedAtValidation && formData.stateCode && derivedAtValidation !== formData.stateCode) {
      newErrors.stateCode = `State mismatch: your GSTIN belongs to ${GST_STATE_CODES[derivedAtValidation]} (${derivedAtValidation}), but you selected ${GST_STATE_CODES[formData.stateCode]} (${formData.stateCode}). Please correct one of them.`
    }

    if((formData.email===undefined || formData.email===null) && (formData.phoneNumber===undefined || formData.phoneNumber===null)){
      newErrors.email = "Session expired. Please go back and sign in again."
    }

    if((formData.token===undefined || formData.token===null) && (formData.idToken===undefined || formData.idToken===null)){
      newErrors.email = "Session expired. Please go back and sign in again."
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    // Create FormData for file upload
      const submitData = new FormData()
      submitData.append("firstName", formData.firstName)
      submitData.append("lastName", formData.lastName)
      if (formData.gstNumber) submitData.append("gstNumber", formData.gstNumber)
      submitData.append("stateCode", formData.stateCode)
      submitData.append("subscriptionCode", formData.subscriptionCode)
      submitData.append("companyName", formData.companyName)
      submitData.append("userType", "normal")
      formData.email? submitData.append("email", formData.email): submitData.append("phoneNumber", formData.phoneNumber)
      formData.token? submitData.append("token", formData.token): submitData.append("idToken", formData.idToken)
      // Address (optional fields — only append non-empty values)
      const address = {}
      if (formData.addressLine1.trim()) address.addressLine1 = formData.addressLine1.trim()
      if (formData.city.trim()) address.city = formData.city.trim()
      if (formData.pincode.trim()) address.pincode = formData.pincode.trim()
      if (Object.keys(address).length > 0) submitData.append("address", JSON.stringify(address))
      if (formData.logo) submitData.append("logo", formData.logo)

    setIsLoading(true)

    try {
      let url = `${API_V1}/auth/google`;
      if(formData.email===undefined || formData.email===null){
        url = `${API_V1}/auth/phone`;
      }

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        body: submitData,
      });
      if(response.status !== 200){
        const errData = await response.json().catch(() => ({}));
        toastFunction("error", errData.message || "Registration failed. Please try again.");
      }else{
        toastFunction("success",`Registration successful! Welcome to ${appName}!`);
        await login();
        navigate('/dashboard');
      }


      

    } catch (error) {
      toastFunction("error","Registration failed. Please check your details and try again.");
    } finally {
      setIsLoading(false)
    }
  }

  // Derived: is there a mismatch between entered GSTIN and selected state?
  const gstinDerivedCode = GSTIN_REGEX.test(formData.gstNumber)
    ? deriveStateCodeFromGSTIN(formData.gstNumber)
    : null
  const gstinStateMismatch =
    gstinDerivedCode !== null &&
    formData.stateCode !== "" &&
    gstinDerivedCode !== formData.stateCode

  return (
    <div className="register-container">
      {/* Left Side - GST Prime Branding */}
      <div className="register-left">
        <div className="brand-section">
          <div className="brand-logo">
            <span className="logo-icon">⚡</span>
            {appName}
          </div>
          <div className="brand-tagline">GST Billing & Inventory Software made for Indian MSMEs</div>
          <div className="brand-subtitle">
            Get your invoicing organised, get paid on time, control your inventory, reduce cost, and grow business
            smoothly.
          </div>
        </div>

        {/* Product Preview */}
        <div className="product-preview">
            <div className="preview-window">
              <div className="window-header">
                <div className="window-dots">
                  <span className="dot red"></span>
                  <span className="dot yellow"></span>
                  <span className="dot green"></span>
                </div>
                <div className="window-title">Create Invoice</div>
              </div>
              <div className="window-content">
                <div className="invoice-form">
                  <div className="form-row">
                    <div className="form-field">
                      <label>Invoice No.</label>
                      <input type="text" value="INV-001" readOnly />
                    </div>
                    <div className="form-field">
                      <label>Date</label>
                      <input type="text" value="Jan 15, 2025" readOnly />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-field full-width">
                      <label>Customer Name</label>
                      <input type="text" value="Acme Corporation" readOnly />
                    </div>
                  </div>
                  <div className="invoice-table">
                    <div className="table-header">
                      <span>Item</span>
                      <span>Qty</span>
                      <span>Rate</span>
                      <span>Amount</span>
                    </div>
                    <div className="table-row">
                      <span>Product A</span>
                      <span>2</span>
                      <span>₹500</span>
                      <span>₹1,000</span>
                    </div>
                    <div className="table-row">
                      <span>Product B</span>
                      <span>1</span>
                      <span>₹750</span>
                      <span>₹750</span>
                    </div>
                  </div>
                  <div className="invoice-total">
                    <div className="total-row">
                      <span>Subtotal:</span>
                      <span>₹1,750</span>
                    </div>
                    <div className="total-row">
                      <span>GST (18%):</span>
                      <span>₹315</span>
                    </div>
                    <div className="total-row final">
                      <span>Total: ₹2,065</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        {/* Feature Benefits */}
        <div className="feature-benefits">
          <div className="benefit-item">
            <div className="benefit-icon">✓</div>
            <span>No payment required • GST Approved • GSTN Integrated</span>
          </div>
          <div className="benefit-item">
            <div className="benefit-icon">✓</div>
            <span>Free 31-day trial • No credit card required</span>
          </div>
          <div className="benefit-item">
            <div className="benefit-icon">✓</div>
            <span>Multi-platform access • Cloud-based solution</span>
          </div>
        </div>
      </div>

      {/* Right Side - Registration Form */}
      <div className="register-right">
        <div className="mobile-brand-bar">
          <span className="logo-icon">⚡</span>
          <span>{appName}</span>
        </div>
        <div className="register-form-container">
          <div className="form-header">
            <h1 className="form-title">Create Your Account</h1>
            <p className="form-subtitle">Start your business journey with {appName}</p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Personal Information */}
            <div className="form-row">
              {/* GST Number */}
              <div className="form-group full-width">
                <label htmlFor="gstNumber" className="form-label">GST Number</label>
                <input
                  type="text"
                  id="gstNumber"
                  name="gstNumber"
                  className={`form-input ${errors.gstNumber ? "error" : ""}`}
                  placeholder="Enter your GST number (optional)"
                  value={formData.gstNumber}
                  onChange={handleInputChange}
                  onBlur={handleGstinBlur}
                  maxLength={15}
                  aria-invalid={!!errors.gstNumber}
                  aria-describedby={errors.gstNumber ? "gstNumber-error" : undefined}
                />
                {gstinLoading && <span className="gstin-input-hint gstin-input-hint--loading">Fetching business details…</span>}
                {errors.gstNumber && <div id="gstNumber-error" className="form-error" role="alert">{errors.gstNumber}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="firstName" className="form-label required">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  className={`form-input ${errors.firstName ? "error" : ""}`}
                  placeholder="Enter your first name"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  aria-invalid={!!errors.firstName}
                  aria-describedby={errors.firstName ? "firstName-error" : undefined}
                />
                {errors.firstName && <div id="firstName-error" className="form-error" role="alert">{errors.firstName}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="lastName" className="form-label required">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  className={`form-input ${errors.lastName ? "error" : ""}`}
                  placeholder="Enter your last name"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  aria-invalid={!!errors.lastName}
                  aria-describedby={errors.lastName ? "lastName-error" : undefined}
                />
                {errors.lastName && <div id="lastName-error" className="form-error" role="alert">{errors.lastName}</div>}
              </div>
            </div>

            {/* Company / Practice Name */}
            <div className="form-group full-width">
              <label htmlFor="companyName" className="form-label required">Company Name</label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                className={`form-input ${errors.companyName ? "error" : ""}`}
                placeholder="Enter your company name"
                value={formData.companyName}
                onChange={handleInputChange}
                aria-invalid={!!errors.companyName}
                aria-describedby={errors.companyName ? "companyName-error" : undefined}
              />
              {errors.companyName && <div id="companyName-error" className="form-error" role="alert">{errors.companyName}</div>}
            </div>


            {/* State (auto-filled from GSTIN, required for GST calculations) */}
            <div className="form-group full-width">
              <label htmlFor="stateCode" className="form-label required">State</label>
              <select
                id="stateCode"
                name="stateCode"
                className={`form-input ${(errors.stateCode || gstinStateMismatch) ? "error" : ""}`}
                value={formData.stateCode}
                onChange={handleInputChange}
                aria-invalid={!!(errors.stateCode || gstinStateMismatch)}
                aria-describedby={errors.stateCode ? "stateCode-error" : gstinStateMismatch ? "stateCode-mismatch" : undefined}
              >
                <option value="">Select your state</option>
                {Object.entries(GST_STATE_CODES).map(([code, name]) => (
                  <option key={code} value={code}>{name} ({code})</option>
                ))}
              </select>
              {gstinStateMismatch && !errors.stateCode && (
                <div id="stateCode-mismatch" className="form-error" role="alert">
                  State mismatch: your GSTIN belongs to {GST_STATE_CODES[gstinDerivedCode]} ({gstinDerivedCode}),
                  but you selected {GST_STATE_CODES[formData.stateCode]} ({formData.stateCode}).
                  Please correct one of them.
                </div>
              )}
              {formData.stateCode && gstinDerivedCode && !gstinStateMismatch && !errors.stateCode && (
                <div className="form-hint">Auto-filled from GSTIN</div>
              )}
              {errors.stateCode && <div id="stateCode-error" className="form-error" role="alert">{errors.stateCode}</div>}
            </div>

            {/* Address (optional) */}
            <div className="form-group full-width">
              <label className="form-label">Address Line 1</label>
              <input
                type="text"
                name="addressLine1"
                className="form-input"
                placeholder="Street address (optional)"
                value={formData.addressLine1}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">City</label>
                <input
                  type="text"
                  name="city"
                  className="form-input"
                  placeholder="City (optional)"
                  value={formData.city}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Pincode</label>
                <input
                  type="text"
                  name="pincode"
                  className="form-input"
                  placeholder="Pincode (optional)"
                  value={formData.pincode}
                  onChange={handleInputChange}
                  maxLength={6}
                />
              </div>
            </div>


            {/* Logo Upload */}
            <div className="form-group full-width">
              <label className="form-label">Company Logo</label>
              <div className="file-upload-container">
                <div
                  className={`file-upload-area ${dragOver ? "drag-over" : ""}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {/* <div className="file-upload-icon">📁</div> */}
                  <div className="file-upload-text">Drag and drop your logo here, or click to browse</div>
                  <div className="file-upload-subtext">PNG, JPG, JPEG(Max 5MB)</div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="file-upload-input"
                    accept="image/*"
                    onChange={handleFileInputChange}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

                {formData.logo && (
                  <div className="file-preview">
                    <img
                      src={URL.createObjectURL(formData.logo) || "/placeholder.svg"}
                      alt="Logo preview"
                      className="file-preview-image"
                    />
                    <div className="file-preview-info">
                      <div className="file-preview-name">{formData.logo.name}</div>
                      <div className="file-preview-size">{formatFileSize(formData.logo.size)}</div>
                    </div>
                    <button type="button" className="file-remove-btn" onClick={handleRemoveFile}>
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="legal-consent-row">
              <input
                type="checkbox"
                id="tos-consent"
                checked={tosAccepted}
                onChange={(e) => setTosAccepted(e.target.checked)}
              />
              <label htmlFor="tos-consent">
                I agree to the{' '}
                <a href="https://www.gstprime.in/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                {' '}and{' '}
                <a href="https://www.gstprime.in/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              </label>
            </div>
            <button type="submit" className="register-button" disabled={isLoading || !tosAccepted}>
              {isLoading ? <span className="loading"></span> : "Create Account"}
            </button>
          </form>

          {/* Login Link */}
          <div className="login-link">
            Already have an account? <button type="button" onClick={() => navigate('/login')} style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: 0, textDecoration: 'underline', fontSize: 'inherit' }}>Sign in</button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RegisterComponent
